// services/fileUploadService.ts - With Thumbnail Support
import { normalizeVideoSettingsViewCrop, normalizeVideoViewCrop } from '../types';
import { getAuth } from 'firebase/auth';
import { setDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { uploadBytes, deleteObject, ref } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { v4 as uuid } from 'uuid';
import {
  getFirstComboId,
  normalizeCombos,
  resolveSegmentOverlapSeconds,
  userVideoDocRef,
  userVideoStorageRef
} from '../utils';
import type { VideoState, SegmentState, VideoSettings, Segment, VideoMeta, CheckpointData } from '../types';
import { VideoThumbnailService } from './videoThumbnailService';
import { validateVideoFileSize, showCompressionGuide } from '../../utils/videoValidation';
import { migrateLegacyTransitionType } from '../transitionCompat';
import { isSupportedVideoFile, resolveVideoUploadType } from '../../utils/videoFileTypes';

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

const parseCheckpointSegments = (segments: unknown): Segment[] => {
  if (!Array.isArray(segments) || segments.length === 0) {
    throw new Error('Invalid checkpoint file: segments array is missing or empty.');
  }

  return segments.map((rawSegment, index) => {
    if (!rawSegment || typeof rawSegment !== 'object') {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} is malformed.`);
    }

    const segment = rawSegment as Partial<Segment>;
    const startTime = segment.startTime;
    const endTime = segment.endTime;
    const transitionDuration = segment.transitionDuration;

    if (!isFiniteNumber(startTime) || startTime < 0) {
      throw new Error(`Invalid segment timing at segment ${index + 1}: startTime must be a non-negative number.`);
    }

    if (!isFiniteNumber(endTime) || endTime <= startTime) {
      throw new Error(`Invalid segment timing at segment ${index + 1}: endTime must be greater than startTime.`);
    }

    if (transitionDuration !== undefined && (!isFiniteNumber(transitionDuration) || transitionDuration < 0)) {
      throw new Error(`Invalid segment timing at segment ${index + 1}: transitionDuration must be a non-negative number.`);
    }

    if (segment.color !== undefined && typeof segment.color !== 'string') {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid color value.`);
    }

    if (segment.name !== undefined && typeof segment.name !== 'string') {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid name value.`);
    }

    if (segment.id !== undefined && typeof segment.id !== 'string') {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid id value.`);
    }

    if (segment.isLearned !== undefined && !isBoolean(segment.isLearned)) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid learned flag.`);
    }
    if (segment.isDisabled !== undefined && !isBoolean(segment.isDisabled)) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid disabled flag.`);
    }
    if (segment.segmentNameEdited !== undefined && !isBoolean(segment.segmentNameEdited)) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid segment title marker.`);
    }
    if (segment.isCombo !== undefined && !isBoolean(segment.isCombo)) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid combo flag.`);
    }
    if (
      segment.sourceSegmentIds !== undefined &&
      (
        !Array.isArray(segment.sourceSegmentIds) ||
        segment.sourceSegmentIds.some(sourceId => typeof sourceId !== 'string')
      )
    ) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has invalid combo source IDs.`);
    }
    if (segment.originSegmentId !== undefined && typeof segment.originSegmentId !== 'string') {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid combo origin ID.`);
    }
    if (segment.comboNameEdited !== undefined && !isBoolean(segment.comboNameEdited)) {
      throw new Error(`Invalid checkpoint file: segment ${index + 1} has an invalid combo name marker.`);
    }

    const parsedSegment: Segment = {
      id: segment.id || `segment-${Date.now()}-${index}`,
      name: segment.name || `Segment ${index + 1}`,
      startTime,
      endTime,
      color: segment.color || '#4ECDC4',
      transitionDuration: transitionDuration ?? 0.5,
      isLearned: segment.isLearned ?? false,
      isDisabled: segment.isDisabled ?? false
    };

    if (segment.isCombo !== undefined) {
      parsedSegment.isCombo = segment.isCombo;
    }
    if (segment.sourceSegmentIds !== undefined) {
      parsedSegment.sourceSegmentIds = segment.sourceSegmentIds;
    }
    if (segment.originSegmentId !== undefined) {
      parsedSegment.originSegmentId = segment.originSegmentId;
    }
    if (segment.segmentNameEdited !== undefined) {
      parsedSegment.segmentNameEdited = segment.segmentNameEdited;
    }
    if (segment.comboNameEdited !== undefined) {
      parsedSegment.comboNameEdited = segment.comboNameEdited;
    }

    return parsedSegment;
  });
};

const validateCheckpointSettings = (
  settings: unknown,
  segments: Segment[]
): CheckpointData['settings'] | null => {
  if (settings === undefined || settings === null) {
    return null;
  }

  if (typeof settings !== 'object') {
    throw new Error('Invalid checkpoint file: settings must be an object.');
  }

  const candidate = settings as Record<string, unknown>;

  if (candidate.defaultSpeed !== undefined) {
    if (!isFiniteNumber(candidate.defaultSpeed) || candidate.defaultSpeed <= 0) {
      throw new Error('Invalid checkpoint file: defaultSpeed must be a positive number.');
    }
  }
  if (candidate.defaultMirror !== undefined && !isBoolean(candidate.defaultMirror)) {
    throw new Error('Invalid checkpoint file: defaultMirror must be true or false.');
  }
  if (candidate.defaultMuted !== undefined && !isBoolean(candidate.defaultMuted)) {
    throw new Error('Invalid checkpoint file: defaultMuted must be true or false.');
  }
  if (candidate.autoLoop !== undefined && !isBoolean(candidate.autoLoop)) {
    throw new Error('Invalid checkpoint file: autoLoop must be true or false.');
  }
  if (candidate.transitionType !== undefined && typeof candidate.transitionType !== 'string') {
    throw new Error('Invalid checkpoint file: transitionType must be a string.');
  }
  if (candidate.segmentOverlapSeconds !== undefined) {
    if (!isFiniteNumber(candidate.segmentOverlapSeconds) || candidate.segmentOverlapSeconds < 0) {
      throw new Error('Invalid checkpoint file: segmentOverlapSeconds must be a non-negative number.');
    }
  }
  if (candidate.segmentOverlap !== undefined) {
    if (!isFiniteNumber(candidate.segmentOverlap) || candidate.segmentOverlap < 0) {
      throw new Error('Invalid checkpoint file: segmentOverlap must be a non-negative number.');
    }
  }

  const transitionMigration = migrateLegacyTransitionType(
    typeof candidate.transitionType === 'string' ? candidate.transitionType : undefined
  );

  const normalizedViewCrop = normalizeVideoViewCrop(candidate.viewCrop);
  const validatedSettings: CheckpointData['settings'] = {
    defaultSpeed: isFiniteNumber(candidate.defaultSpeed) ? candidate.defaultSpeed : 1.0,
    defaultMirror: isBoolean(candidate.defaultMirror) ? candidate.defaultMirror : true,
    defaultMuted: isBoolean(candidate.defaultMuted) ? candidate.defaultMuted : false,
    autoLoop: isBoolean(candidate.autoLoop) ? candidate.autoLoop : false,
    transitionType: transitionMigration.transitionType,
    segmentOverlapSeconds: resolveSegmentOverlapSeconds(candidate, segments)
  };

  if (normalizedViewCrop) {
    validatedSettings.viewCrop = normalizedViewCrop;
  }

  return validatedSettings;
};

export class FileUploadService {
  /**
   * Clean up old video URL
   */
  static cleanupVideoUrl(videoSrc: string | null): void {
    if (videoSrc && videoSrc.startsWith('blob:')) {
      URL.revokeObjectURL(videoSrc);
    }
  }

  /**
   * Handle video file upload
   */
  static handleVideoFile(
    file: File,
    currentVideoSrc: string | null,
    setVideoState: (updates: Partial<VideoState>) => void,
    setSegmentState: (updates: Partial<SegmentState>) => void,
    setUIState: (updates: { isDemo: boolean }) => void,
    setOriginalVideoFile: (file: File | null) => void
  ): boolean {  // Returns false if rejected
    console.log('🔍 Validating video file:', file.name, `(${(file.size / 1024 / 1024).toFixed(1)} MB)`);

    if (file.size <= 0) {
      alert('This video file is empty (0 bytes). Please choose a valid video file.');
      return false;
    }

    if (!isSupportedVideoFile(file)) {
      alert('Unsupported file type. Please upload a valid video file (for example: .mp4, .mov, .webm).');
      return false;
    }
    
    // Validate file size
    const validation = validateVideoFileSize(file);
    
    // If file is too large, reject and show compression guide
    if (!validation.valid && validation.error) {
      alert(validation.error);
      
      // Show compression guide
      const showGuide = window.confirm(
        'Would you like to see how to compress your video?'
      );
      
      if (showGuide) {
        alert(showCompressionGuide(validation.sizeMB));
      }
      
      return false;  // Don't load the file
    }
    
    // If file is large but acceptable, show warning
    if (validation.warning) {
      const proceed = window.confirm(validation.warning);
      if (!proceed) {
        return false;  // User chose not to proceed
      }
    }
    
    // File is valid, proceed with upload
    console.log('✅ File size validation passed');

    // Clean up previous video
    this.cleanupVideoUrl(currentVideoSrc);

    // Create object URL for new video
    const videoUrl = URL.createObjectURL(file);
    
    setVideoState({
      videoSrc: videoUrl,
      videoFileName: file.name,
      isLoading: true,
      loadingSource: 'local',
      currentTime: 0,
      isPlaying: false,
      viewCrop: undefined
    });

    // Reset segments
    setSegmentState({
      segments: [],
      activeSegment: null,
      activeComboId: null,
      isMarkingSegment: false,
      currentSegmentStart: null,
      isLooping: false,
      transitionType: 'none'
    });

    // Set demo flag to false and store original file
    setUIState({ isDemo: false });
    setOriginalVideoFile(file);

    return true;  // File loaded successfully
  }

  /**
   * Save video to library WITH THUMBNAIL
   * @param file - Video file
   * @param title - Video title
   * @param segments - Video segments
   * @param settings - Video settings
   * @returns Promise with video ID and metadata
   */
  static async saveToLibrary(
    file: File, 
    title: string,
    segments: Segment[],
    settings: VideoSettings
  ): Promise<{ id: string; meta: Omit<VideoMeta, 'id'> }> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const uid = user.uid;

    const id = uuid();
    const { contentType, extension } = await resolveVideoUploadType(file);
    const storageRef = userVideoStorageRef(uid, id, extension);

    console.log('🟡 Starting video save with thumbnail...');

    // STEP 1: Extract and upload thumbnail
    let thumbnailPath: string | undefined;
    try {
      console.log('🟡 Attempting to extract thumbnail from video file...');
      thumbnailPath = await VideoThumbnailService.extractAndUploadThumbnail(
        file,
        uid,
        id,
        0 // Capture at 0.5 seconds (or pass 0 for smart capture (25% will be calculated inside)
      );
      console.log('🟢 Thumbnail created successfully:', thumbnailPath);
    } catch (error) {
      console.error('❌ Failed to create thumbnail:', error);
      console.warn('⚠️ Continuing without thumbnail - video will use placeholder');
      // Continue without thumbnail - not critical
    }

    // STEP 2: Upload video to Storage
    console.log('🟡 Uploading video to Storage...');
    await uploadBytes(storageRef, file, {
      cacheControl: 'public, max-age=31536000', // 1 year cache
      contentType
    });
    console.log('🟢 Video uploaded');

    // STEP 3: Prepare Firestore metadata with thumbnail path
    const normalizedSegments = normalizeCombos(segments);
    const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
    const meta: any = {
      storagePath: storageRef.fullPath,
      title: title || file.name,
      size: file.size,
      segments: normalizedSegments,
      settings: normalizedSettings,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Only add thumbnailPath if it's defined (Firestore rejects undefined)
    if (thumbnailPath) {
      meta.thumbnailPath = thumbnailPath;
    }

    // STEP 4: Write Firestore doc
    console.log('🟡 Saving metadata to Firestore...');
    await setDoc(userVideoDocRef(uid, id), meta);
    console.log('🟢 Metadata saved');

    return { id, meta };
  }

  /**
   * Delete a video from the library (including thumbnail)
   * @param videoId - Video ID to delete
   * @param storagePath - Stored object path; required for non-mp4 uploads, since
   *                      the id alone cannot tell us the container extension.
   */
  static async deleteFromLibrary(videoId: string, storagePath?: string): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const uid = user.uid;

    try {
      // Delete video from Storage
      const storageRef = storagePath
        ? ref(storage, storagePath)
        : userVideoStorageRef(uid, videoId);
      await deleteObject(storageRef);
      console.log('🟢 Video deleted from Storage');

      // Delete thumbnail from Storage
      const thumbnailPath = `users/${uid}/thumbnails/${videoId}.jpg`;
      await VideoThumbnailService.deleteThumbnail(thumbnailPath);

      // Delete from Firestore
      const docRef = userVideoDocRef(uid, videoId);
      await deleteDoc(docRef);
      console.log('🟢 Video metadata deleted from Firestore');
    } catch (error) {
      console.error('🔴 Error deleting video:', error);
      throw new Error('Failed to delete video: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  /**
   * Update segments for an existing video in the library
   * @param videoId - Video ID
   * @param segments - Updated segments
   * @param settings - Updated settings
   */
  static async updateSegments(
    videoId: string,
    segments: Segment[],
    settings: VideoSettings
  ): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const uid = user.uid;

    const docRef = userVideoDocRef(uid, videoId);
    const { updateDoc } = await import('firebase/firestore');
    const normalizedSettings = normalizeVideoSettingsViewCrop(settings) as VideoSettings;
    
    await updateDoc(docRef, {
      segments: normalizeCombos(segments),
      settings: normalizedSettings,
      updatedAt: serverTimestamp()
    });
    
    console.log('🟢 Segments updated in library');
  }

  /**
   * Load demo video and segments
   */
  static async loadDemo(
    currentVideoSrc: string | null,
    setVideoState: (updates: Partial<VideoState>) => void,
    setSegmentState: (updates: Partial<SegmentState>) => void,
    setUIState: (updates: { isDemo: boolean }) => void,
    setOriginalVideoFile: (file: File | null) => void
  ): Promise<void> {
    // Clean up previous video
    this.cleanupVideoUrl(currentVideoSrc);

    try {
      // Import demo utilities
      const { loadDemoData, getDemoVideoUrl } = await import('../utils');
      const demoData = await loadDemoData();
      const normalizedSegments = normalizeCombos(demoData.segments);
      const demoVideoUrl = getDemoVideoUrl();

      setVideoState({
        videoSrc: demoVideoUrl,
        videoFileName: demoData.videoFile,
        isLoading: true,
        loadingSource: 'demo',
        currentTime: 0,
        isPlaying: false,
        viewCrop: normalizeVideoViewCrop(demoData.settings?.viewCrop)
      });

      setSegmentState({
        segments: normalizedSegments,
        activeSegment: null,
        activeComboId: getFirstComboId(normalizedSegments),
        isMarkingSegment: false,
        currentSegmentStart: null,
        isLooping: false,
        transitionType: 'none'
      });

      setUIState({ isDemo: true });
      setOriginalVideoFile(null);
    } catch (error) {
      console.error('Failed to load demo:', error);
      throw error;
    }
  }

  /**
   * Handle checkpoint file load
   */
  static async handleCheckpointLoad(
    file: File,
    setSegmentState: (updates: Partial<SegmentState>) => void,
    setVideoState: (updates: Partial<VideoState>) => void,
    videoElement: HTMLVideoElement | null
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const parsedData = JSON.parse(text) as { segments?: unknown; settings?: unknown };
          
          const validatedSegments = normalizeCombos(parseCheckpointSegments(parsedData.segments));
          const validatedSettings = validateCheckpointSettings(parsedData.settings, validatedSegments);

          const segmentUpdates: Partial<SegmentState> = {
            segments: validatedSegments,
            activeSegment: null,
            activeComboId: getFirstComboId(validatedSegments),
            isMarkingSegment: false,
            currentSegmentStart: null
          };
          
          // Update video state if settings are present
          if (validatedSettings) {
            const updates: Partial<VideoState> = {};
            
            if (validatedSettings.defaultSpeed !== undefined) {
              updates.playbackRate = validatedSettings.defaultSpeed;
            }
            if (validatedSettings.defaultMirror !== undefined) {
              updates.isMirrored = validatedSettings.defaultMirror;
            }
            if (validatedSettings.defaultMuted !== undefined) {
              updates.userMutePreference = validatedSettings.defaultMuted;
            }
            updates.viewCrop = validatedSettings.viewCrop;
            
            setVideoState(updates);

            // Apply playback rate to video element if available
            if (videoElement && validatedSettings.defaultSpeed !== undefined) {
              videoElement.playbackRate = validatedSettings.defaultSpeed;
            }

            // Update loop settings if present
            if (validatedSettings.autoLoop !== undefined) {
              segmentUpdates.isLooping = validatedSettings.autoLoop;
            }
            if (validatedSettings.transitionType !== undefined) {
              segmentUpdates.transitionType = validatedSettings.transitionType;
            }
            if (validatedSettings.segmentOverlapSeconds !== undefined) {
              segmentUpdates.segmentOverlap = validatedSettings.segmentOverlapSeconds;
            }
          }

          setSegmentState(segmentUpdates);
          
          console.log('🟢 Checkpoints loaded successfully');
          resolve();
        } catch (error) {
          if (error instanceof Error) {
            reject(error);
          } else {
            reject(new Error('Failed to parse checkpoint file. Please ensure it is valid JSON.'));
          }
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read checkpoint file'));
      };
      
      reader.readAsText(file);
    });
  }

  /**
   * Update segments for an existing video (alias for updateSegments)
   */
  static async updateLibrarySegments(
    videoId: string,
    segments: Segment[],
    settings: VideoSettings
  ): Promise<void> {
    return this.updateSegments(videoId, segments, settings);
  }

  /**
   * Create video settings object from state
   * @param videoState - Current video state
   * @param segmentState - Current segment state
   * @returns VideoSettings object
   */
  static createSettingsFromState(
    videoState: VideoState,
    segmentState: SegmentState
  ): VideoSettings {
    const settings: VideoSettings = {
      defaultSpeed: videoState.playbackRate,
      defaultMirror: videoState.isMirrored,
      defaultMuted: videoState.userMutePreference,
      autoLoop: segmentState.isLooping,
      transitionType: segmentState.transitionType || 'none',
      segmentOverlapSeconds: segmentState.segmentOverlap,
      timelineZoom: videoState.timelineZoom ?? 1,
      activeSegmentId: segmentState.activeSegment ?? null
    };

    const normalizedViewCrop = normalizeVideoViewCrop(videoState.viewCrop);
    if (normalizedViewCrop) {
      settings.viewCrop = normalizedViewCrop;
    }

    return settings;
  }
}

export default FileUploadService;
